/* Bilingual field resolvers — every CMS read in the site goes through here.
 *
 * Mirrors the contract productName() established in components/shared.js:
 * prefer the requested language, fall back to the other language, then to
 * a hardcoded default. That last tier is what makes the CMS migration
 * safe to ship in pieces — a field that has not been authored yet, or one
 * an editor accidentally blanks, renders the copy the site shipped with
 * rather than an empty gap.
 *
 * These work on BOTH the legacy `name` object type and the new
 * `localeString` / `localeText` types. The two are structurally identical
 * ({english, french}) and this reads them the same way, which is why the
 * two can coexist without migrating 1551 documents.
 */

const EN = 'EN';
const FR = 'FR';

/* Pick the best available string from a {english, french} object.
   Whitespace-only counts as empty — an editor who types a space into a
   field they meant to leave blank should still get the fallback. */
const pickString = (field, language) => {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  const english = (field.english || '').trim();
  const french = (field.french || '').trim();
  return language === FR ? french || english : english || french;
};

/**
 * Resolve a bilingual string.
 *
 * @param {object|string} field    A {english, french} object from Sanity.
 * @param {'EN'|'FR'}     language The active language.
 * @param {object|string} fallback Either a plain string or another
 *   {english, french} object, so fallbacks can be authored in the same
 *   shape as the CMS values they stand in for.
 */
export const t = (field, language, fallback) => {
  const value = pickString(field, language);
  if (value) return value;
  if (typeof fallback === 'string') return fallback;
  return pickString(fallback, language);
};

/**
 * Resolve a multi-paragraph field into an array of paragraphs.
 *
 * Blank-line separation is the existing render contract in equipments.js
 * and manufacturers.js, so migrated content needs no transformation.
 */
export const paragraphs = (field, language, fallback) =>
  t(field, language, fallback)
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

/**
 * Resolve a localeEmphasis field into {before, accent, after}.
 *
 * Falls back a whole phrase at a time rather than field by field — a
 * French `before` paired with an English `accent` would read as broken,
 * so if the requested language's phrase is empty we take the other
 * language's phrase intact.
 */
export const resolveEmphasis = (field, language, fallback) => {
  const hasContent = (phrase) =>
    phrase && (phrase.before || phrase.accent || phrase.after);

  const pickPhrase = (source) => {
    if (!source) return null;
    const requested = language === FR ? source.french : source.english;
    const other = language === FR ? source.english : source.french;
    if (hasContent(requested)) return requested;
    if (hasContent(other)) return other;
    return null;
  };

  const phrase = pickPhrase(field) || pickPhrase(fallback) || {};
  return {
    before: phrase.before || '',
    accent: phrase.accent || '',
    after: phrase.after || ''
  };
};

/**
 * Resolve a localeRichText field into a Portable Text array.
 *
 * Reads the value out of a `_raw<Field>` GraphQL alias — gatsby-source-
 * sanity only generates those for top-level document fields, which is
 * why localeRichText fields must never be nested inside another object.
 */
export const richText = (raw, language, fallback) => {
  const pickArray = (source) => {
    if (!source) return null;
    const requested = language === FR ? source.french : source.english;
    const other = language === FR ? source.english : source.french;
    if (Array.isArray(requested) && requested.length) return requested;
    if (Array.isArray(other) && other.length) return other;
    return null;
  };
  return pickArray(raw) || pickArray(fallback) || null;
};

/**
 * Substitute {placeholders} in a template string.
 *
 * Unknown placeholders are left in place rather than replaced with
 * "undefined" — a visible {count} in the output is a much clearer signal
 * that something needs fixing than a silently mangled sentence.
 */
export const fill = (str, vars = {}) =>
  String(str || '').replace(/\{(\w+)\}/g, (match, key) =>
    key in vars ? String(vars[key]) : match
  );

/**
 * Resolve a localePlural field for a given count.
 *
 * Stores four complete sentences rather than a stem plus a suffix rule,
 * because French can require agreement at several points from one count
 * ("vos {count} articles sélectionnés"). Writing the whole sentence
 * handles any number of agreement points without a mini-syntax.
 */
export const plural = (field, count, language, vars = {}, fallback) => {
  const source = field || fallback || {};
  const isFrench = language === FR;

  const one =
    (isFrench ? source.frenchOne : source.englishOne) || source.englishOne || '';
  const other =
    (isFrench ? source.frenchOther : source.englishOther) ||
    source.englishOther ||
    '';

  return fill(count === 1 ? one : other, { count, ...vars });
};

export { EN, FR };
